/**
 * AI CONTEXT
 * Purpose: quick-save standout moments from TeacherView with note/save at top, then category/tag flow.
 * Uses: shared standout hook + KeywordPicker + analytics.
 * Writes: standout moments (and optional generated feedback tracking).
 * Used by: TeacherClassInfo.
 * Layout: "Quick story" label, then note + Save Moment on one row (2px primary border, 12px padding, two lines tall),
 * then a hint line saying where the story goes, then category Segmented, then KeywordPicker.
 * A note alone can save. Keywords stay optional. The selected category is stored with the note.
 * The story attaches to the selected session when this student counts as in
 * class there, including ENROLLED. That does not write attendance. An absent
 * or planned-absent student still stamps the nearest session they were marked
 * present or tardy for, and save is blocked when there is none.
 * Refactor: align shared behavior with Evaluation StandoutMomentsPanel to reduce divergence.
 */
import { CheckOutlined } from "@ant-design/icons"
import { App, Button, Input, Segmented, Typography } from "antd"
import { useCallback, useMemo, useState } from "react"

import Flex from "#blocks/Flex"
import { useThemeContext } from "#contexts/ThemeContextProvider"
import { useUserContext } from "#contexts/UserContextProvider"
import {
  StandoutCategory,
  TagPool,
} from "#customTypes/composedTypes/standoutTypes"
import { Uuid } from "#customTypes/globalTypes"
import useAnalytics from "#hooks/useAnalytics"
import { COLORS } from "#styles/colors"

import KeywordPicker from "../EvaluationView/StandoutMoments/KeywordPicker"
import { noAttendedSessionMessage } from "../EvaluationView/StandoutMoments/nearestAttendedSession"
import { useNearestAttendedSession } from "../EvaluationView/StandoutMoments/useNearestAttendedSession"
import {
  SortMode,
  useStandoutMoments,
} from "../EvaluationView/StandoutMoments/useStandoutMoments"

import { teacherStorySessionId } from "./teacherStorySession"

const { TextArea } = Input
const { Text } = Typography

const CATEGORY_OPTIONS: { label: string; value: StandoutCategory }[] = [
  { label: "Thumbs Up", value: "thumbsUp" },
  { label: "Needs Work", value: "needsWork" },
  { label: "Secret", value: "secret" },
]

const CATEGORY_POOL_MAP: Record<StandoutCategory, TagPool> = {
  thumbsUp: "skill",
  needsWork: "skill",
  secret: "secret",
}

interface QuickTagPanelProps {
  studentId: Uuid
  studentName: string
  semester: string
  curriculumId: Uuid
  classId?: Uuid
  // Current session's project — stamped onto the moment for the review
  // dashboard (older moments infer it from the curriculum schedule).
  projectId?: Uuid
  // Selected session when this student counts as in class there. The story
  // attaches to it and attendance is left unchanged. Omitted when they are
  // absent or planned-absent; the panel then uses the nearest attended session.
  inClassSessionId?: Uuid
  onSaved: () => void
}

type CategoryState = {
  selectedTagIds: Set<Uuid>
  sortMode: SortMode
  note: string
}

const defaultCategoryState = (): CategoryState => ({
  selectedTagIds: new Set(),
  sortMode: "frequency",
  note: "",
})

export default function StandoutMomentPanel({
  studentId,
  studentName,
  semester,
  curriculumId,
  classId,
  projectId,
  inClassSessionId,
  onSaved,
}: QuickTagPanelProps) {
  const theme = useThemeContext()
  const { effectiveUserId } = useUserContext()
  const { trackStandoutAction, ANALYTICS_EVENTS } = useAnalytics()
  const { message } = App.useApp()
  const [category, setCategory] = useState<StandoutCategory>("thumbsUp")
  const [categoryStates, setCategoryStates] = useState<
    Record<StandoutCategory, CategoryState>
  >({
    thumbsUp: defaultCategoryState(),
    needsWork: defaultCategoryState(),
    secret: defaultCategoryState(),
  })
  const [isSaving, setIsSaving] = useState(false)

  const { tagsById, getSortedTags, createCustomTag, saveStandoutMoment } =
    useStandoutMoments(studentId, semester, curriculumId)
  const {
    ready: nearestReady,
    sessionId: nearestSessionId,
    whenReady,
  } = useNearestAttendedSession(studentId)
  const sessionId = teacherStorySessionId(inClassSessionId, nearestSessionId)
  const ready = inClassSessionId ? true : nearestReady

  const currentState = categoryStates[category]
  const pool = CATEGORY_POOL_MAP[category]
  const sortedTags = useMemo(
    () => getSortedTags(pool, currentState.sortMode, category),
    [getSortedTags, pool, currentState.sortMode, category]
  )

  const updateCategoryState = useCallback(
    (cat: StandoutCategory, patch: Partial<CategoryState>) => {
      setCategoryStates((prev) => ({
        ...prev,
        [cat]: { ...prev[cat], ...patch },
      }))
    },
    []
  )

  const handleToggleTag = useCallback(
    (tagId: Uuid) => {
      setCategoryStates((prev) => {
        const current = prev[category]
        const next = new Set(current.selectedTagIds)
        if (next.has(tagId)) {
          next.delete(tagId)
        } else {
          next.add(tagId)
        }
        return { ...prev, [category]: { ...current, selectedTagIds: next } }
      })
    },
    [category]
  )

  const handleRemoveTag = useCallback(
    (tagId: Uuid) => {
      setCategoryStates((prev) => {
        const current = prev[category]
        const next = new Set(current.selectedTagIds)
        next.delete(tagId)
        return { ...prev, [category]: { ...current, selectedTagIds: next } }
      })
    },
    [category]
  )

  const segmentedOptions = useMemo(
    () =>
      CATEGORY_OPTIONS.map((opt) => {
        const count = categoryStates[opt.value].selectedTagIds.size
        return {
          ...opt,
          label: count > 0 ? `${opt.label} (${count})` : opt.label,
        }
      }),
    [categoryStates]
  )

  const noteText = currentState.note.trim()
  const hasContent =
    currentState.selectedTagIds.size >= 1 || noteText.length > 0
  const canSave = hasContent && ready && !!sessionId
  const blockedMessage =
    ready && !sessionId ? noAttendedSessionMessage(studentName) : undefined

  const handleSave = useCallback(async () => {
    const stampSessionId = inClassSessionId
      ? inClassSessionId
      : (await whenReady()).sessionId
    if (!hasContent || !stampSessionId) return

    setIsSaving(true)
    try {
      const tagIdArray = Array.from(currentState.selectedTagIds)
      await saveStandoutMoment(category, tagIdArray, {
        note: noteText || undefined,
        classId,
        projectId,
        classSessionId: stampSessionId,
      })
      trackStandoutAction(ANALYTICS_EVENTS.STANDOUT_MOMENT_SAVED, {
        teacherId: effectiveUserId,
        studentId,
        category,
        tagCount: tagIdArray.length,
        tagTexts: tagIdArray
          .map((id) => tagsById[id]?.text)
          .filter(Boolean) as string[],
        hasNote: noteText.length > 0,
        classId,
      })
      void message.success(`Moment saved for ${studentName}`)
      updateCategoryState(category, {
        selectedTagIds: new Set(),
        note: "",
      })
      onSaved()
    } catch {
      void message.error("Failed to save moment")
    } finally {
      setIsSaving(false)
    }
  }, [
    hasContent,
    currentState,
    saveStandoutMoment,
    category,
    classId,
    projectId,
    whenReady,
    inClassSessionId,
    noteText,
    message,
    studentName,
    onSaved,
    trackStandoutAction,
    ANALYTICS_EVENTS,
    effectiveUserId,
    studentId,
    tagsById,
    updateCategoryState,
  ])

  const firstName = studentName.trim().split(/\s+/)[0] || studentName
  const storyInputId = `quick-story-${studentId}`

  return (
    <Flex
      col
      gap={8}
      style={{
        marginTop: 8,
        width: "100%",
        padding: 12,
        borderRadius: 8,
        border: `1px solid ${theme.eric.blue}22`,
        backgroundColor: theme.eric.blue + "08",
      }}
    >
      <label htmlFor={storyInputId}>
        <Text strong>Quick story</Text>{" "}
        <Text type="secondary" style={{ fontSize: 12 }}>
          the one thing worth telling
        </Text>
      </label>
      <Flex gap={8} wrap style={{ alignItems: "center" }}>
        <TextArea
          id={storyInputId}
          autoFocus
          placeholder="What happened today? A quote, a build, a breakthrough..."
          value={currentState.note}
          onChange={(e) =>
            updateCategoryState(category, { note: e.target.value })
          }
          autoSize={{ minRows: 2, maxRows: 4 }}
          styles={{ textarea: { padding: 12 } }}
          style={{
            flex: "1 1 160px",
            border: `2px solid ${COLORS.theme.colorPrimary}`,
            padding: 12,
          }}
        />
        <Button
          type="primary"
          icon={<CheckOutlined />}
          onClick={handleSave}
          disabled={!canSave}
          loading={isSaving}
          size="small"
          style={{
            backgroundColor: canSave ? theme.eric.green : undefined,
          }}
        >
          Save Moment
        </Button>
      </Flex>
      <Text
        type="secondary"
        data-testid="quick-story-hint"
        style={{ fontSize: 12, marginTop: -4 }}
      >
        Goes to the family in {firstName}&rsquo;s next update, and onto the
        profile.
      </Text>
      {blockedMessage ? (
        <Text
          type="danger"
          data-testid="standout-no-attended-session"
          style={{ fontSize: 12 }}
        >
          {blockedMessage}
        </Text>
      ) : null}

      <Segmented
        options={segmentedOptions}
        value={category}
        onChange={(val) => setCategory(val as StandoutCategory)}
        size="small"
        block
      />

      <KeywordPicker
        minRequired={0}
        previewLimit={6}
        pool={pool}
        sortedTags={sortedTags}
        selectedTagIds={currentState.selectedTagIds}
        sortMode={currentState.sortMode}
        onSortModeChange={(mode) =>
          updateCategoryState(category, { sortMode: mode })
        }
        onToggleTag={handleToggleTag}
        onRemoveTag={handleRemoveTag}
        onCreateCustomTag={createCustomTag}
        tagsById={tagsById}
      />
    </Flex>
  )
}
